import { Badge, Button, Table } from '@arcflight/tf-component-library';
import React, { ReactNode, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTable, useSortBy, usePagination } from 'react-table';
import styled from 'styled-components';
import { Trip } from '../../models/trips';
import arrowIcon from '../../assets/icon-arrow.svg';
import plusIcon from '../../assets/plus.svg';
import FlightDrawer, { SectionHeader } from '../FlightDrawer/FlightDrawer';
import { changeDrawerContent, changeDrawerMode } from '../../models/drawer';

interface TripSectorsProps {
  trip: Trip;
  sectors: any[];
}

const ContentWrapper = styled.div``;

const StyledSpan = styled.span`
  text-transform: capitalize;
  display: flex;
  flex-shrink: 0;
  align-items: center;
`;

const BadgeWrapper = styled.div`
  margin-right: 8px;
`;

const ArrowWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const SectionWrapper = styled.div`
  margin-bottom: 32px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TripSectors: React.FC<TripSectorsProps> = ({ trip, sectors }) => {
  const dispatch = useDispatch();

  const handleSectorClick = (e): void => {
    const { id } = e.original;
    dispatch(
      changeDrawerContent({
        payload: { content: <FlightDrawer flight={id} />, backButton: true },
      }),
    );
  };

  const handleAddSector = (): void => {
    dispatch(
      changeDrawerContent({
        payload: { content: <FlightDrawer trip={trip} />, backButton: true },
      }),
    );
    dispatch(changeDrawerMode({ payload: 'add' }));
  };

  const data = useMemo(() => sectors, [sectors]);
  const columns = useMemo(
    () => [
      {
        Header: 'Status',
        accessor: 'status',
        width: 100,
        Cell: ({ value }): ReactNode => {
          let color = 'green';
          if (value === 'partial' || value === 'in_progress') color = 'amber';
          if (value === 'planned') color = 'grey';
          return value ? (
            <StyledSpan>
              <BadgeWrapper>
                <Badge color={color} />
              </BadgeWrapper>
              {value.replace(/_/g, ' ')}
            </StyledSpan>
          ) : (
            '-'
          );
        },
      },
      {
        Header: 'No.',
        width: 60,
        Cell: ({ row }): number => {
          return row.index + 1;
        },
      },
      {
        Header: 'From',
        accessor: 'departure_airport',
        width: 100,
        Cell: ({ value }): string => value || '-',
      },
      {
        Header: 'To',
        accessor: 'arrival_airport',
        width: 100,
        Cell: ({ value }): string => value || '-',
      },
      {
        Header: 'title',
        // accessor: 'status',
        Cell: ({ value }): string => value || '-',
        width: 100,
      },
      {
        width: 50,
        id: 'arrow',
        Cell: (): ReactNode => (
          <ArrowWrapper>
            <img src={arrowIcon} alt="arrow icon" />
          </ArrowWrapper>
        ),
      },
    ],
    [],
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { hiddenColumns: [] },
    },
    useSortBy,
    usePagination,
  );
  return (
    <SectionWrapper data-testid="TripDrawer-TripSectors">
      <ButtonWrapper>
        <SectionHeader reducedBottomMargin marginLeft="20px" data-testid="TripSectors-Header">
          Sectors
        </SectionHeader>
        {trip?.id ? (
          <Button height="24px" onClick={handleAddSector}>
            <img src={plusIcon} alt="plus icon" /> Add sector
          </Button>
        ) : null}
      </ButtonWrapper>
      <ContentWrapper>
        <Table tableInstance={tableInstance} handleRowClick={handleSectorClick} />
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default TripSectors;
